import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { ScrollArea } from "@/components/ui/scroll-area"
import { getAlfredBulkUpdateFailureDetails } from "@/lib/alfred-bulk-update"
import type { AlfredBulkUpdateDeviceIdentity } from "@/lib/alfred-bulk-update"
import type { BulkUpdateAlfredDevicesResult } from "@/types/alfred-device"

interface BulkUpdateFailureDescriptionProps {
  result: BulkUpdateAlfredDevicesResult
  deviceIdentityByPrimaryKey: ReadonlyMap<number, AlfredBulkUpdateDeviceIdentity>
}

function DeviceIdentity({
  deviceId,
  clientId,
}: AlfredBulkUpdateDeviceIdentity) {
  return (
    <span className="text-foreground">
      <span className="font-semibold">Device ID:</span> {deviceId},{" "}
      <span className="font-semibold">Client ID:</span> {clientId}
    </span>
  )
}

export function BulkUpdateFailureDescription({
  result,
  deviceIdentityByPrimaryKey,
}: BulkUpdateFailureDescriptionProps) {
  const failures = getAlfredBulkUpdateFailureDetails(
    result,
    deviceIdentityByPrimaryKey,
  )
  // The endpoint uses 4xx/5xx only when every device fails; partial success is 207.
  const allDevicesFailed =
    result.successCount === 0 &&
    result.failureCount === result.requestedCount
  const failureGroups = [...failures.reduce((groups, failure) => {
    const group = groups.get(failure.message)
    if (group) group.push(failure)
    else groups.set(failure.message, [failure])
    return groups
  }, new Map<string, typeof failures>()).entries()].map(([message, devices]) => ({
    message,
    devices,
  }))
  const allFailuresShareMessage =
    allDevicesFailed && failureGroups.length <= 1
  const sharedFailureMessage =
    failures[0]?.message ?? "The devices could not be updated."

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="link" size="sm">
          {allDevicesFailed ? "View failed devices" : "View device errors"}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {allDevicesFailed ? "Bulk update failed" : "Bulk update errors"}
          </DialogTitle>
          <DialogDescription>
            {result.failureCount} device
            {result.failureCount === 1 ? "" : "s"} could not be updated.
          </DialogDescription>
        </DialogHeader>
        {allFailuresShareMessage && (
          <Alert variant="destructive">
            <AlertTitle>Reason</AlertTitle>
            <AlertDescription>{sharedFailureMessage}</AlertDescription>
          </Alert>
        )}
        <div className="flex flex-col gap-2">
          {allFailuresShareMessage && (
            <p className="text-sm font-medium text-foreground">
              Failed devices
            </p>
          )}
          <ScrollArea className="h-[50vh] max-h-[28rem] pr-4">
            {!allFailuresShareMessage ? (
              <div className="flex flex-col gap-6">
                <p className="text-sm font-medium text-foreground">
                  Failures by reason
                </p>
                {failureGroups.map((group) => (
                  <section
                    key={group.message}
                    aria-label={group.message}
                    className="flex flex-col gap-2 break-words"
                  >
                    <h3 className="font-semibold text-destructive">
                      {group.message}
                    </h3>
                    <ul className="flex flex-col gap-2 pl-4">
                      {group.devices.map((failure) => (
                        <li key={failure.id}>
                          <DeviceIdentity
                            deviceId={failure.deviceId}
                            clientId={failure.clientId}
                          />
                        </li>
                      ))}
                    </ul>
                  </section>
                ))}
              </div>
            ) : (
              <ul className="flex flex-col gap-4">
                {failures.map((failure) => (
                  <li key={failure.id} className="break-words">
                    <DeviceIdentity
                      deviceId={failure.deviceId}
                      clientId={failure.clientId}
                    />
                    {!allDevicesFailed && (
                      <p className="text-muted-foreground">{failure.message}</p>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </ScrollArea>
        </div>
      </DialogContent>
    </Dialog>
  )
}
